import { type FC, useEffect, useState } from 'react'
import styled from '@emotion/styled'

import { useTheme } from '@emotion/react'

import { delay } from 'lodash'

import {
  BoxLogo,
  RitualLogo,
  IntuitLogo,
  DominosLogo,
  AllerganLogo,
  VmwareLogo,
  LevisLogo,
  InstacartLogo,
  IbmLogo,
} from '@segment/matcha/components/CompanyLogos/Company'

import Divider from '@segment/matcha/components/Divider'

import * as CopyText from '@segment/matcha/recipes/CopyText'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import DemoForm from '@segment/templates/Forms/DemoForm/'

import { getTrackingPreferences } from '@segment/tracking/scripts'

const logosCopy = {
  heading: `You're in great company`,
  description: `Join 25,000+ companies who are using Segment to power world-class customer experiences.`,
}

const DRIFT_CAMPAIGN_ID = 2777417

const Hero: FC = () => {
  const theme = useTheme()
  const [acceptsFunctionalCookies, setAcceptsFunctionalCookies] = useState(false)

  const copy = {
    overline: `Get a demo`,
    heading: `See what Segment can do for you`,
    description: `Tell us a little about yourself and we'll connect you with a Segment expert who can share more about the product and answer any questions you have.`,
  }

  // Copy that goes before the form and is hidden after form submission
  const beforeContent = (
    <>
      <CopyText.Heading variant="h1" tag="h2">
        {copy.heading}
      </CopyText.Heading>
      <CopyText.Description variant="pLarge" tag="p">
        {copy.description}
      </CopyText.Description>
    </>
  )

  useEffect(() => {
    // Delay to make sure the cookie is set
    delay(() => setAcceptsFunctionalCookies(Boolean(getTrackingPreferences()?.custom.functional)), 1000)
  }, [])

  return (
    <Wrapper>
      <HeroBase variant="2Col">
        <HeroRecipe.Copy>
          <DemoForm
            customFormIdentifier="site-fastlane-demo"
            customTrackingType="site-fastlane-demo"
            before={beforeContent}
            isLeadEnrichmentFormEnabled={!acceptsFunctionalCookies}
            driftCampaignId={DRIFT_CAMPAIGN_ID}
          ></DemoForm>
        </HeroRecipe.Copy>

        <StyledContents>
          <CopyText.Heading variant="h3">{logosCopy.heading}</CopyText.Heading>
          <CopyText.Description>{logosCopy.description}</CopyText.Description>

          <Divider
            tokens={{
              color: theme.palette.brand.darkAcai[300],
            }}
          />
          <LogoGrid>
            <BoxLogo />
            <RitualLogo />
            <IntuitLogo />
            <DominosLogo />
            <AllerganLogo />
            <VmwareLogo />
            <LevisLogo />
            <InstacartLogo />
            <IbmLogo />
          </LogoGrid>
        </StyledContents>
      </HeroBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  align-items: flex-start;

  ${HeroRecipe.Wrapper} {
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      grid-template-columns: 1fr 1fr;
    }
    ${({ theme }) => theme.breakpoints.up(`xl`)} {
      gap: 200px;
    }
  }
`

const StyledContents = styled(HeroRecipe.Contents)`
  justify-items: stretch;
  width: auto;
`

const LogoGrid = styled.div`
  align-items: center;
  display: grid;
  gap: 32px;
  grid-template-columns: 1fr 1fr 1fr;
  justify-items: center;
  padding: 48px 0;

  svg {
    height: 32px;
  }
`

export default Hero
